<div *ngIf="!form" class="text-center py-5">
  <div class="spinner-border text-primary" role="status">
    <span class="visually-hidden">Loading...</span>
  </div>
  <p class="mt-3 text-muted">Loading tender details...</p>
</div>

<div class="container py-5 has-floating-actions" *ngIf="form" [formGroup]="form">
  <div class="row justify-content-center">
    <div class="col-lg-8">
      <!-- Parameters Card -->
      <div class="card shadow-sm border-0 rounded-4 overflow-hidden mb-4">
        <div class="card-header bg-light-subtle py-4 border-bottom border-light">
          <div class="d-flex align-items-center">
            <div class="icon-box bg-primary-subtle text-primary rounded-3 p-2 me-3">
              <i class="bi bi-sliders2 fs-4"></i>
            </div>
            <div>
              <h3 class="mb-0 fw-bold">Parameters</h3>
              <p class="text-muted mb-0">Configure tender settings</p>
            </div>
          </div>
        </div>

        <div class="card-body p-4">
          <div class="mb-4">
            <label class="form-label-custom">Tender Name</label>
            <input type="text" class="form-control py-2 px-3 border-light-subtle rounded-3"
              placeholder="e.g., Europe 2026 - 2027" formControlName="name" data-testid="tender-name"
              [ngClass]="getFormGroupErrorClasses(form.controls.name)">
            <div class="invalid-feedback" *ngIf="showErrorBlock(form.controls.name)">
              <p *ngIf="form.controls.name.errors.required" class="mb-0">{{ getValidationMessage('name', 'required')}}
              </p>
            </div>
            <div class="form-hint">Give this tender a name for easy reference</div>
          </div>

          <div class="row g-4 mb-4">
            <div class="col-md-6">
              <label class="form-label-custom">Preferred Currency</label>
              <jft-currency-select data-testid="tender-currency"
                [filterFields]="tender.company.settings.getFilterFields('currency')"
                [allowClear]="false" [settings]="tender.company.settings" [currencyId]="form.get('currency_id').value"
                (onChange)="form.get('currency_id').patchValue($event); form.markAsDirty()">
              </jft-currency-select>
              <div class="invalid-feedback d-block" *ngIf="showErrorBlock(form.controls.currency_id)">
                <p *ngIf="form.controls.currency_id.errors.required" class="mb-0">{{ getValidationMessage('currency_id',
                  'required')}}</p>
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label-custom">Preferred Units</label>
              <jft-units-select data-testid="tender-units" [allowClear]="false"
                [filterFields]="tender.company.settings.getFilterFields('units')"
                [settings]="tender.company.settings"
                (onChange)="form.get('units_id').patchValue($event); form.markAsDirty()"
                [unitId]="form.get('units_id').value"></jft-units-select>
              <div class="invalid-feedback d-block" *ngIf="showErrorBlock(form.controls.units_id)">
                <p *ngIf="form.controls.units_id.errors.required" class="mb-0">{{ getValidationMessage('units_id',
                  'required')}}</p>
              </div>
            </div>

          </div>

          <div class="mb-4">
            <label class="form-label-custom">Round One Closing Date</label>
            <div class="input-icon-wrapper">
              <input type="text" class="form-control py-2 px-3 border-light-subtle rounded-3" placeholder="dd.mm.yyyy"
                formControlName="date_round_end" data-testid="tender-round-end" ngbDatepicker #d1="ngbDatepicker"
                [minDate]="minDateRoundEnd"
                [readOnly]="true" (click)="d1.toggle()"
                [ngClass]="getFormGroupErrorClasses(form.controls.date_round_end)">
              <i class="bi bi-calendar3 input-icon" (click)="d1.toggle()"></i>
            </div>
            <div class="invalid-feedback d-block"
              *ngIf="showErrorBlock(form.controls.date_round_end) || form.controls.date_round_end.errors?.ngbDate?.minDate">
              <p *ngIf="form.controls.date_round_end.errors?.required" class="mb-0">{{
                getValidationMessage('date_round_end', 'required')}}</p>
              <p *ngIf="form.controls.date_round_end.errors?.ngbDate?.minDate" class="mb-0">
                {{ getValidationMessage('date_round_end', 'requiredBefore')}}
              </p>
            </div>
            <div class="form-hint">Round will end at 00:00 UTC on the selected date</div>
          </div>

          <div class="row g-4">
            <div class="col-md-6">
              <label class="form-label-custom">Contract Start Date</label>
              <div class="input-icon-wrapper">
                <input type="text" class="form-control py-2 px-3 border-light-subtle rounded-3" placeholder="dd.mm.yyyy"
                  formControlName="date_contract_start" data-testid="tender-contract-start" ngbDatepicker
                  #d2="ngbDatepicker" [minDate]="minStartDate"
                  [readOnly]="true" (click)="d2.toggle()"
                  [ngClass]="getFormGroupErrorClasses(form.controls.date_contract_start)">
                <i class="bi bi-calendar3 input-icon" (click)="d2.toggle()"></i>
              </div>
              <div class="invalid-feedback d-block"
                *ngIf="showErrorBlock(form.controls.date_contract_start) || form.controls.date_contract_start.errors?.ngbDate?.minDate">
                <p *ngIf="form.controls.date_contract_start.errors?.required" class="mb-0">{{
                  getValidationMessage('date_contract_start', 'required')}}</p>
                <p *ngIf="form.controls.date_contract_start.errors?.ngbDate?.minDate" class="mb-0">
                  {{ getValidationMessage('date_contract_start', 'requiredBefore')}}
                </p>
              </div>
            </div>
            <div class="col-md-6">
              <label class="form-label-custom">Contract End Date</label>
              <div class="input-icon-wrapper">
                <input type="text" class="form-control py-2 px-3 border-light-subtle rounded-3" placeholder="dd.mm.yyyy"
                  formControlName="date_contract_end" data-testid="tender-contract-end" ngbDatepicker
                  #d3="ngbDatepicker" [minDate]="minEndDate"
                  [readOnly]="true" (click)="d3.toggle()"
                  [ngClass]="getFormGroupErrorClasses(form.controls.date_contract_end)">
                <i class="bi bi-calendar3 input-icon" (click)="d3.toggle()"></i>
              </div>
              <div class="invalid-feedback d-block"
                *ngIf="showErrorBlock(form.controls.date_contract_end) || form.controls.date_contract_end.errors?.ngbDate?.minDate">
                <p *ngIf="form.controls.date_contract_end.errors?.required" class="mb-0">{{
                  getValidationMessage('date_contract_end', 'required')}}</p>
                <p *ngIf="form.controls.date_contract_end.errors?.ngbDate?.minDate" class="mb-0">
                  {{ getValidationMessage('date_contract_end', 'requiredBefore')}}
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <!-- Cover Letter Card -->
      <div class="card shadow-sm border-0 rounded-4 overflow-hidden mb-4">
        <div class="card-header bg-light-subtle py-4 border-bottom border-light">
          <div class="d-flex align-items-center">
            <div class="icon-box bg-primary-subtle text-primary rounded-3 p-2 me-3">
              <i class="bi bi-envelope-paper fs-4"></i>
            </div>
            <div>
              <h3 class="mb-0 fw-bold">Cover Letter</h3>
              <p class="text-muted mb-0">Add the cover letter information for this tender</p>
            </div>
          </div>
        </div>
        <div class="card-body p-4">
          <div class="jft-tabs mb-4">
            <button class="jft-tab" [class.active]="coverLetterType === 'text'" type="button"
              (click)="setCoverLetterType('text')">Compose Text</button>
            <button class="jft-tab" [class.active]="coverLetterType === 'file'" type="button"
              (click)="setCoverLetterType('file')">Upload File</button>
          </div>

          <div *ngIf="coverLetterType === 'text'">
            <textarea class="form-control form-control-textarea border-light-subtle" data-testid="tender-cover-letter"
              placeholder="Paste the text of your cover letter here..." [(ngModel)]="coverLetterText"
              (ngModelChange)="updateCoverLetterFormValue()" [ngModelOptions]="{standalone: true}"></textarea>
            <div class="form-hint">This text will be presented in the tender invitation section</div>
          </div>

          <div *ngIf="coverLetterType === 'file'">
            <div *ngIf="coverLetterFile" class="uploaded-files-grid mb-4">
              <div class="uploaded-file-card border-primary">
                <div class="d-flex align-items-center">
                  <div class="file-icon me-3 shadow-sm">
                    <i class="bi bi-file-earmark-text"></i>
                  </div>
                  <div class="file-info flex-grow-1 overflow-hidden">
                    <div class="file-name text-truncate" [title]="coverLetterFile.name">{{ coverLetterFile.name }}</div>
                    <div class="file-subtext">Cover Letter Document</div>
                  </div>
                  <button type="button" class="btn btn-remove p-2 border-0 bg-transparent" (click)="removeCoverLetter()"
                    title="Change document">
                    <i class="bi bi-x-lg"></i>
                  </button>
                </div>
              </div>
            </div>

            <jft-dropzone *ngIf="!coverLetterFile" (filesSelected)="uploadCoverLetter($event)"
              [loading]="coverLetterUploadInProgress" [multiple]="false"
              [accept]="'.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt'">
              <div class="upload-zone-content py-4">
                <div class="upload-icon mb-2"><i class="bi bi-file-earmark-arrow-up text-primary"></i></div>
                <div class="upload-text">
                  <span class="fs-5 d-block mb-1 fw-bold">Drag & drop your file here</span>
                  or <strong class="text-primary cursor-pointer">Browse from computer</strong>
                </div>
              </div>
            </jft-dropzone>
            <div class="form-hint">Upload your cover letter (PDF, Word, Excel, Image or Text, max 10MB)</div>
          </div>
        </div>
      </div>

      <!-- Documents Card -->
      <div class="card shadow-sm border-0 rounded-4 overflow-hidden mb-5">
        <div class="card-header bg-light-subtle py-4 border-bottom border-light">
          <div class="d-flex align-items-center">
            <div class="icon-box bg-primary-subtle text-primary rounded-3 p-2 me-3">
              <i class="bi bi-file-earmark-arrow-up fs-4"></i>
            </div>
            <div>
              <h3 class="mb-0 fw-bold">Supporting Documents</h3>
              <p class="text-muted mb-0">Upload additional files</p>
            </div>
          </div>
        </div>
        <div class="card-body p-4">
          <div *ngIf="documents.length" class="uploaded-files-grid mb-4">
            <div *ngFor="let document of documents" class="uploaded-file-card">
              <div class="d-flex align-items-center">
                <div class="file-icon me-3 shadow-sm">
                  <i class="bi bi-file-earmark-text"></i>
                </div>
                <div class="file-info flex-grow-1 overflow-hidden">
                  <div class="file-name text-truncate" [title]="document.name">{{ document.name }}</div>
                  <div class="file-subtext">Supporting Document</div>
                </div>
                <button type="button" class="btn btn-remove p-2 border-0 bg-transparent"
                  (click)="removeDocument(document)" title="Remove document">
                  <i class="bi bi-x-lg"></i>
                </button>
              </div>
            </div>
          </div>

          <jft-dropzone (filesSelected)="uploadDocuments($event)" [loading]="uploadInProgress" [multiple]="true"
            [accept]="'.pdf,.doc,.docx,.xls,.xlsx,.png,.jpg,.jpeg,.txt'">
            <div class="upload-zone-content py-4">
              <div class="upload-icon mb-2"><i class="bi bi-cloud-arrow-up-fill text-primary"></i></div>
              <div class="upload-text">
                <span class="fs-5 d-block mb-1 fw-bold">Drag & drop files here</span>
                or <strong class="text-primary cursor-pointer">Browse from computer</strong>
              </div>
            </div>
          </jft-dropzone>
          <div class="form-hint">Max file size 10MB. PDF, Word, Excel, Images and Text supported.</div>
        </div>
      </div>
    </div>

    <!-- Sidebar -->
    <div class="col-lg-4">
      <div class="sidebar-card shadow-sm border-0 rounded-4 sticky-top mb-4" style="top: 2rem;">
        <h5 class="fw-bold mb-3">Setup Guide</h5>
        <div class="checklist-item">
          <div class="checklist-circle"></div>
          <div>
            <div class="fw-bold mb-1">Clear Tender Name</div>
            <div class="small text-muted">Help participants identify your tender easily.</div>
          </div>
        </div>
        <div class="checklist-item">
          <div class="checklist-circle"></div>
          <div>
            <div class="fw-bold mb-1">Contact Information</div>
            <div class="small text-muted">Include technical and legal contacts in your cover letter.</div>
          </div>
        </div>
        <div class="checklist-item">
          <div class="checklist-circle"></div>
          <div>
            <div class="fw-bold mb-1">Terms & Conditions</div>
            <div class="small text-muted">Upload your latest T&Cs to the documents section.</div>
          </div>
        </div>

      </div>
    </div>
  </div>
</div>

<jft-submission-footer *ngIf="form" data-testid="wizard-next" backLabel="Cancel"
  [nextLabel]="form.dirty ? 'Save & Continue' : 'Continue to Locations'" nextIcon="bi-arrow-right"
  secondaryNextLabel="Delete Draft" secondaryNextClass="btn btn-link text-danger text-decoration-none fw-bold"
  [nextLoading]="form.disabled" [nextLoadingTitle]="'Saving...'" [nextDisabled]="!form.valid"
  [validationErrors]="formErrors ? [formErrors] : []" (onBack)="goBack()" (onNext)="onSubmit()"
  (onSecondaryNext)="deleteTender()">
</jft-submission-footer>